import React from 'react';

const suitSymbols = { spades: "♠", hearts: "♥", diamonds: "♦", clubs: "♣" };
const suitColors = { spades: "#333", clubs: "#333", hearts: "#d22", diamonds: "#d22" };
const SUIT_KEYS = ["spades", "hearts", "diamonds", "clubs"];

/**
 * PhaseControls Component
 * Phase-specific action controls for the local player.
 * Pass compact for casino HUD density (classic TableCentric leaves this unset).
 */
export default function PhaseControls({ 
  controls, 
  phase, 
  gameState,
  socket, 
  tableID, 
  playerID,
  compact = false,
}) {
  const panelClass = compact ? 'cv2-phase-panel' : undefined;
  const wrapStyle = compact ? compactContainerStyle : containerStyle;
  const primaryStyle = compact ? compactPrimaryButtonStyle : primaryButtonStyle;
  const actionStyle = compact ? compactActionButtonStyle : actionButtonStyle;

  // Debug logging for action phase
  if (phase === 'action') {
    console.log(`DEBUG: PhaseControls - phase: ${phase}, controls.showKnockDiscard: ${controls.showKnockDiscard}, activePlayerIdx: ${gameState.activePlayerIdx}, playerID: ${playerID}`);
  }
  
  // Waiting phase - Ready button
  if (controls.showReadyButton) {
    console.log(`DEBUG: PhaseControls - Rendering Ready button. tableID: ${tableID}, playerID: ${playerID}, socket:`, socket ? 'connected' : 'null');
    return (
      <div className={panelClass} style={wrapStyle}>
        <button
          onClick={() => {
            console.log(`DEBUG: PhaseControls - Ready button clicked. Emitting readyForSuitVote. tableID: ${tableID}, playerID: ${playerID}`);
            if (!socket) {
              console.error(`ERROR: PhaseControls - Socket is null!`);
              return;
            }
            socket.emit('readyForSuitVote', { tableID, playerID });
          }}
          style={primaryStyle}
        >
          Ready
        </button>
      </div>
    );
  }
  
  // Suit vote phase - Suit selection (immediate on click, no confirm button)
  if (controls.showSuitVote) {
    // Check if player has already voted
    const hasVoted = gameState.suitVotes?.some(v => v.playerID === playerID);
    const votedSuit = hasVoted ? gameState.suitVotes.find(v => v.playerID === playerID)?.suit : null;
    
    return (
      <div
        className={panelClass}
        style={compact
          ? wrapStyle
          : {...containerStyle, padding: '10px', width: '100%', maxWidth: '280px', boxSizing: 'border-box'}}
      >
        <div className={compact ? 'cv2-phase-heading' : undefined} style={{ marginBottom: compact ? '4px' : '6px', textAlign: 'center' }}>
          <div style={{ fontSize: compact ? '0.72rem' : '0.8rem', fontWeight: '600', color: compact ? 'var(--cv2-muted, #c4b8a0)' : '#1e293b', marginBottom: '2px' }}>
            Vote for Dealer Suit
          </div>
          {hasVoted && (
            <div style={{ fontSize: '0.65rem', color: '#10b981', fontWeight: '500' }}>
              Voted: {votedSuit ? suitSymbols[votedSuit] : ''}
            </div>
          )}
        </div>
        
        <div className={compact ? 'cv2-suit-row' : undefined} style={{ display: 'flex', gap: compact ? '6px' : '6px', justifyContent: 'center' }}>
          {SUIT_KEYS.map(suit => {
            const isSelected = votedSuit === suit;
            const isDisabled = hasVoted;
            
            return (
              <button
                key={suit}
                className={compact ? 'cv2-suit-btn' : undefined}
                onClick={() => {
                  if (!hasVoted) {
                    socket.emit('suitVote', { tableID, playerID, suit });
                  }
                }}
                disabled={isDisabled}
                style={{
                  ...suitButtonStyle,
                  width: compact ? '48px' : '50px',
                  height: compact ? '48px' : '60px',
                  background: isSelected ? '#3b82f6' : '#f1f5f9',
                  color: isSelected ? 'white' : '#1e293b',
                  border: isSelected ? '2px solid #2563eb' : '2px solid #e2e8f0',
                  transform: isSelected ? 'scale(1.05)' : 'scale(1)',
                  transition: 'all 0.2s ease',
                  opacity: isDisabled && !isSelected ? 0.5 : 1,
                  cursor: isDisabled ? 'not-allowed' : 'pointer'
                }}
              >
                <div className={compact ? 'cv2-suit-glyph' : undefined} style={{ fontSize: compact ? '1.05rem' : '1.2rem', marginBottom: '2px' }}>
                  {suitSymbols[suit]}
                </div>
                <div className={compact ? 'cv2-suit-label' : undefined} style={{ fontSize: compact ? '0.5rem' : '0.55rem', fontWeight: '600' }}>
                  {suit.charAt(0).toUpperCase() + suit.slice(1)}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  }
  
  // Show suit selected notice during dealer_reveal and dealer_selected phases
  const showSuitSelected = (phase === 'dealer_reveal' || phase === 'dealer_selected') && gameState.dealerSuit;
  
  // Dealer reveal/selected phase - Ready to Deal
  if (controls.showDealerReady) {
    return (
      <div className={panelClass} style={wrapStyle}>
        {showSuitSelected && (
          <div style={{ 
            marginBottom: compact ? '6px' : '12px', 
            padding: compact ? '4px 6px' : '8px', 
            background: compact ? 'rgba(59,130,246,0.15)' : '#eff6ff', 
            borderRadius: '6px', 
            border: '1px solid #3b82f6',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '0.7rem', color: compact ? 'var(--cv2-muted, #c4b8a0)' : '#64748b', marginBottom: '2px' }}>
              Suit selected:
            </div>
            <div style={{ fontSize: compact ? '1rem' : '1.2rem', color: suitColors[gameState.dealerSuit] || '#3b82f6', fontWeight: '700' }}>
              {suitSymbols[gameState.dealerSuit] || gameState.dealerSuit}
            </div>
          </div>
        )}
        <div className={compact ? 'cv2-phase-heading' : undefined} style={{ marginBottom: compact ? '6px' : '12px', textAlign: 'center' }}>
          <div style={{ fontSize: compact ? '0.85rem' : '1rem', fontWeight: '600', color: compact ? 'var(--cv2-text, #f5efe3)' : '#1e293b', marginBottom: '2px' }}>
            Dealer Selected
          </div>
          <div style={{ fontSize: compact ? '0.72rem' : '0.875rem', color: compact ? 'var(--cv2-muted, #c4b8a0)' : '#64748b' }}>
            Ready to deal cards?
          </div>
        </div>
        <button
          onClick={() => socket.emit('dealerSelectedReady', { 
            tableID, 
            playerID 
          })}
          style={primaryStyle}
        >
          Ready to Deal
        </button>
      </div>
    );
  }
  
  // Show suit selected notice during dealer_reveal phase (when ready button isn't shown yet)
  if (showSuitSelected && phase === 'dealer_reveal') {
    return (
      <div className={panelClass} style={wrapStyle}>
        <div style={{ 
          padding: compact ? '8px' : '12px', 
          background: compact ? 'rgba(59,130,246,0.15)' : '#eff6ff', 
          borderRadius: '6px', 
          border: '1px solid #3b82f6',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '0.75rem', color: compact ? 'var(--cv2-muted, #c4b8a0)' : '#64748b', marginBottom: '4px' }}>
            Suit selected:
          </div>
          <div style={{ fontSize: compact ? '1.2rem' : '1.5rem', color: suitColors[gameState.dealerSuit] || '#3b82f6', fontWeight: '700' }}>
            {suitSymbols[gameState.dealerSuit] || gameState.dealerSuit}
          </div>
          <div style={{ fontSize: '0.7rem', color: compact ? 'var(--cv2-muted, #c4b8a0)' : '#64748b', marginTop: '6px' }}>
            Revealing dealer...
          </div>
        </div>
      </div>
    );
  }
  
  // Action phase - Knock/Discard (server phase is authoritative; never show after hand ends)
  if (controls.showKnockDiscard && phase === 'action') {
    return (
      <div className={panelClass} style={wrapStyle}>
        <div className={compact ? 'cv2-phase-heading' : undefined} style={{ marginBottom: compact ? '6px' : '12px', textAlign: 'center' }}>
          <div style={{ fontSize: compact ? '0.85rem' : '1rem', fontWeight: '600', color: compact ? 'var(--cv2-text, #f5efe3)' : '#1e293b', marginBottom: '2px' }}>
            Your Turn
          </div>
          <div style={{ fontSize: compact ? '0.72rem' : '0.875rem', color: compact ? 'var(--cv2-muted, #c4b8a0)' : '#64748b' }}>
            Choose your action
          </div>
        </div>
        
        <div className={compact ? 'cv2-action-row' : undefined} style={{ display: 'flex', gap: compact ? '8px' : '12px', justifyContent: 'center' }}>
          <button
            onClick={() => socket.emit('playerAction', { tableID, playerID, action: 'knock' })}
            style={{
              ...actionStyle,
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              borderColor: '#047857'
            }}
          >
            Knock
          </button>
          <button
            onClick={() => socket.emit('playerAction', { tableID, playerID, action: 'discard' })}
            style={{
              ...actionStyle,
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              borderColor: '#b91c1c'
            }}
          >
            Discard
          </button>
        </div>
      </div>
    );
  }
  
  // Results phase - Ready for Next Round
  if (controls.showNextRound && phase === 'results') {
    return (
      <div className={panelClass} style={wrapStyle}>
        <button
          onClick={() => socket.emit('nextRoundReady', { tableID, playerID })}
          style={primaryStyle}
        >
          Ready for Next Round
        </button>
      </div>
    );
  }
  
  // No controls for current phase
  return null;
}

const containerStyle = {
  background: '#ffffff',
  borderRadius: '12px',
  padding: '16px',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  border: '1px solid #e2e8f0',
  width: '100%',
  maxWidth: '280px',
  boxSizing: 'border-box'
};

const compactContainerStyle = {
  background: 'transparent',
  borderRadius: '10px',
  padding: '8px 10px',
  boxShadow: 'none',
  border: 'none',
  width: '100%',
  maxWidth: '100%',
  boxSizing: 'border-box'
};

const primaryButtonStyle = {
  width: '100%',
  padding: '12px 24px',
  fontSize: '1rem',
  fontWeight: '700',
  color: 'white',
  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
  boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
  transition: 'all 0.2s ease'
};

const compactPrimaryButtonStyle = {
  ...primaryButtonStyle,
  padding: '10px 16px',
  fontSize: '0.92rem',
  minHeight: '44px',
  boxShadow: 'none',
};

const actionButtonStyle = {
  flex: 1,
  padding: '14px 24px',
  fontSize: '1rem',
  fontWeight: '700',
  color: 'white',
  border: '2px solid',
  borderRadius: '8px',
  cursor: 'pointer',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
  transition: 'all 0.2s ease'
};

const compactActionButtonStyle = {
  ...actionButtonStyle,
  padding: '10px 16px',
  fontSize: '0.92rem',
  minHeight: '44px',
  boxShadow: 'none',
};

const suitButtonStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '8px',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  border: '2px solid #e2e8f0'
};
